<template>
    <div>
      <div class="d-flex align-center breadcrumb-back-row">
        <breadcrumb :items="breadcrumbs" />
        <span class="breadcrumb-back-link primary--text d-flex align-center cursor-pointer" @click="goBack">
          <v-icon color="primary" size="20" class="mr-1">mdi-chevron-left</v-icon> {{ $t('carbonCredit.back') }}
        </span>
      </div>
      <v-container fluid>
      <v-card elevation="0">
        <v-row class="pt-4">
          <!-- Left: Form -->
          <v-col cols="12" md="4">
            <v-card class="pa-6 pt-0" elevation="0">
              <h3 class="font-weight-bold mb-6">{{ $t('carbonCredit.plotTreeMapping.chooseFarmLocation') }}</h3>
  
              <v-form ref="locationForm" v-model="isFormValid">
                <label class="text-caption text-grey-darken-1 mb-1">
                  {{ $t('carbonCredit.plotTreeMapping.longitude') }} 
                </label>
                <v-text-field v-model="longitude" outlined dense class="mb-2" :rules="longitudeRules" type="number"
                  step="any" :placeholder="$t('carbonCredit.plotTreeMapping.enterLongitude')" readonly />
  
                <label class="text-caption text-grey-darken-1 mb-1">
                  {{ $t('carbonCredit.plotTreeMapping.latitude') }} 
                </label>
                <v-text-field v-model="latitude" outlined dense class="mb-2" :rules="latitudeRules" type="number"
                  step="any" :placeholder="$t('carbonCredit.plotTreeMapping.enterLatitude')" readonly />
  
                <label class="text-caption text-grey-darken-1 mb-1 d-block">
                  {{ $t('carbonCredit.plotTreeMapping.farmAreaSize') }}
                </label>
                <v-row no-gutters class="d-flex align-center mb-4">
                  <v-col cols="7" sm="7" md="7">
                    <v-text-field
                      v-model="areaSize"
                      type="number"
                      step="any"
                      placeholder="1"
                      outlined
                      dense
                      class="farm-area-input"
                      :rules="areaSizeRules"
                    />
                  </v-col>
                  <v-col cols="5" sm="5" md="5" class="farm-area-unit-col pb-6">
                    <v-select
                      :items="areaUnitOptions"
                      item-text="label"
                      item-value="value"
                      v-model="areaUnit"
                      dense
                      outlined
                      hide-details
                      class="farm-area-unit rounded-l-0 bg-primary white--text"
                    />
                  </v-col>
                </v-row>

                <div class="d-flex justify-space-between mt-2">
                  <v-btn outlined color="primary" class="mr-2" @click="onCancel" :disabled="loading"
                    style="min-width: 120px">
                    {{ $t('cancel') }}
                  </v-btn>
                  <v-btn color="primary" dark @click="onSave" :loading="loading" style="min-width: 120px">
                    {{ $t('save') }}
                  </v-btn>
                </div>
              </v-form>
            </v-card>
          </v-col>
  
          <!-- Right: Map -->
          <v-col cols="12" md="8" class="pa-0">
            <div class="map-wrapper">
              <div class="search-container">
                <v-text-field 
                  ref="searchInput"
                  prepend-inner-icon="mdi-magnify" 
                  v-model="search"
                  :placeholder="$t('carbonCredit.plotTreeMapping.searchAddress')" 
                  outlined 
                  dense 
                  class="map-search"
                  @keyup.enter="searchLocation"
                  @input="onSearchInput"
                  @focus="showAutocomplete = true"
                  @blur="handleSearchBlur"
                />
                <!-- Autocomplete dropdown -->
                <v-card v-if="showAutocomplete && autocompleteResults.length > 0" class="autocomplete-list" elevation="5">
                  <v-list dense>
                    <v-list-item
                      v-for="(result, index) in autocompleteResults"
                      :key="index"
                      @click="selectAutocompleteResult(result)"
                    >
                      <v-list-item-icon class="mr-3">
                        <v-icon>mdi-map-marker</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ result.description }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </div>
              <div class="map-inner">
                <GmapMap 
                  :center="mapCenter" 
                  :zoom="18" 
                  map-type-id="satellite" 
                  style="width: 100%; height: 100%;"
                  @click="onMapClick"
                  :options="mapOptions"
                >
                  <GmapMarker 
                    v-if="markerPosition"
                    :position="markerPosition" 
                    :draggable="true" 
                    @dragend="onMarkerDragEnd"
                    :clickable="false"
                  />
                </GmapMap>
              </div>
              <v-btn 
                class="mx-2 float-right" 
                fab 
                dark 
                small 
                color="white"
                style="margin-top: -39vh;" 
                @click="gotoCurrentLocation"
              >
                <v-icon dark color="gray">
                  mdi-crosshairs-gps
                </v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
    </div>
  </template>
  
  <script>
  import loadingMixin from "@/mixins/LoadingMixin";
  
  export default {
    name: 'FarmLocation',
  
    mounted() {
      const saved = this.$store.getters['carbonCredit/getFarmLocation'];
      if (saved) {
        if (saved.longitude != null) this.longitude = String(saved.longitude);
        if (saved.latitude != null) this.latitude = String(saved.latitude);
        if (saved.address) this.search = saved.address;
        if (saved.areaSize != null && saved.areaSize !== '') this.areaSize = saved.areaSize;
        if (saved.areaUnit && saved.areaUnit !== 'sq_meters') this.areaUnit = saved.areaUnit;
      }
      this.initializeMap();
    },
  
    data() {
      return {
        loading: false,
        isFormValid: false,
        longitude: '',
        latitude: '',
        radius: 0,
        additionalNotes: '',
        search: '',
        mapCenter: { lat: -12.0464, lng: -77.0428 },
        markerPosition: null,
        mapOptions: {
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: 'cooperative'
        },
        longitudeRules: [
          v => !!v || 'Longitude is required',
          v => !isNaN(v) || 'Longitude must be a number',
          v => (v >= -180 && v <= 180) || 'Longitude must be between -180 and 180'
        ],
        latitudeRules: [
          v => !!v || 'Latitude is required',
          v => !isNaN(v) || 'Latitude must be a number',
          v => (v >= -90 && v <= 90) || 'Latitude must be between -90 and 90'
        ],
        areaSizeRules: [
          v => (v != null && v !== '') || 'Farm area size is required',
          v => !isNaN(parseFloat(v)) || 'Farm area size must be a number',
          v => parseFloat(v) > 0 || 'Farm area size must be greater than 0'
        ],
        showAutocomplete: false,
        autocompleteResults: [],
        searchTimeout: null,
        reverseGeocodeTimeout: null,
        areaSize: '',
        areaUnit: 'hectares',
        areaUnitOptions: [
          { label: 'Hectares', value: 'hectares' },
          { label: 'Acres', value: 'acres' }
        ]
      }
    },

    computed: {
      breadcrumbs() {
        const requestId = this.$route.query.requestId;
        const addPlotHref = requestId
          ? `/carbon-credit/tree-plot-add/${requestId}`
          : '/carbon-credit/tree-plot-add';
        const addPlotText = requestId
          ? this.$t('carbonCredit.plotTreeMapping.editPlotTreeMappingRequest')
          : this.$t('carbonCredit.plotTreeMapping.addPlotTree');
        return [
          {
            text: this.$t('carbonCredit.dashboard'),
            disabled: false,
            href: '/dashboard',
            exact: true,
          },
          {
            text: this.$t('carbonCredit.title'),
            disabled: false,
            href: '/carbon-credit/projects',
          },
          {
            text: this.$t('carbonCredit.plotTreeMapping.title'),
            disabled: false,
            href: '/carbon-credit/plot-tree-mapping',
          },
          {
            text: addPlotText,
            disabled: false,
            href: addPlotHref,
          },
          {
            text: this.$t('carbonCredit.plotTreeMapping.chooseFarmLocation'),
            disabled: true,
            href: '#',
            exact: true,
          },
        ];
      },
    },

    watch: {
      longitude(newVal) {
        if (newVal && this.latitude) {
          this.updateMarkerPosition();
        }
      },
      latitude(newVal) {
        if (newVal && this.longitude) {
          this.updateMarkerPosition();
        }
      }
    },
  
    mixins: [loadingMixin],
    methods: {
      goBack() {
        this.navigateBackToAddPlotTree();
      },
  
      onCancel() {
        this.navigateBackToAddPlotTree();
      },

      navigateBackToAddPlotTree() {
        const requestId = this.$route.query.requestId;
        this.$router.push({
          name: 'treePlotAdd',
          params: requestId ? { id: requestId } : {}
        });
      },
  
      updateMarkerPosition() {
        if (this.longitude && this.latitude) {
          const lat = parseFloat(this.latitude);
          const lng = parseFloat(this.longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            this.markerPosition = { lat, lng };
            this.mapCenter = { lat, lng };
            this.updateAddressFromCoordinates(lat, lng);
          }
        }
      },

      updateAddressFromCoordinates(lat, lng) {
        if (this.reverseGeocodeTimeout) clearTimeout(this.reverseGeocodeTimeout);
        this.reverseGeocodeTimeout = setTimeout(() => {
          this.reverseGeocode({ lat, lng });
          this.reverseGeocodeTimeout = null;
        }, 400);
      },
  
      onMapClick(event) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        
        // Update form fields
        this.latitude = lat.toFixed(6);
        this.longitude = lng.toFixed(6);
        
        // Move only the marker, not the map center
        this.markerPosition = { lat, lng };

        // Reverse geocode to get the address
        this.reverseGeocode({ lat, lng });
      },
  
      onMarkerDragEnd(event) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        
        // Update form fields
        this.latitude = lat.toFixed(6);
        this.longitude = lng.toFixed(6);
        
        // Update marker position and address
        this.markerPosition = { lat, lng };
        this.reverseGeocode({ lat, lng });
      },
  
      async searchLocation() {
        if (!this.search.trim()) return;
  
        this.startLoading();
  
        try {
          // Wait for Google Maps API to be available
          if (typeof window.google === 'undefined' || !window.google.maps) {
            // If Google Maps API is not loaded, try to load it
            await this.loadGoogleMapsAPI();
          }
  
          // Create a geocoder instance
          const geocoder = new window.google.maps.Geocoder();
          
          // Search for the address
          geocoder.geocode({ address: this.search }, (results, status) => {
            this.stopLoading();
            
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              const lat = location.lat();
              const lng = location.lng();
              
              // Update form fields
              this.latitude = lat.toFixed(6);
              this.longitude = lng.toFixed(6);
              
              // Move marker to the searched location
              this.markerPosition = { lat, lng };
              
              // Center the map on the searched location
              this.mapCenter = { lat, lng };
            } else {
              this.$notify({
                title: 'Error',
                text: 'Location not found. Please try a different search term.',
                type: 'error'
              });
            }
          });
        } catch (error) {
          this.stopLoading();
          console.error('Error searching location:', error);
          this.$notify({
            title: 'Error',
            text: 'Error searching location. Please try again.',
            type: 'error'
          });
        }
      },
  
      loadGoogleMapsAPI() {
        return new Promise((resolve, reject) => {
          // Check if Google Maps API is already loaded
          if (typeof window.google !== 'undefined' && window.google.maps) {
            resolve();
            return;
          }
  
          // If not loaded, show a message to the user
          this.$notify({
            title: 'Info',
            text: 'Google Maps API is loading. Please try searching again in a moment.',
            type: 'info'
          });
          
          // Wait a bit and try again
          setTimeout(() => {
            if (typeof window.google !== 'undefined' && window.google.maps) {
              resolve();
            } else {
              reject(new Error('Google Maps API not available'));
            }
          }, 2000);
        });
      },
  
      initializeMap() {
        // If we already have coordinates (e.g. from saved farm location), use them for map/marker
        if (this.longitude && this.latitude) {
          const lat = parseFloat(this.latitude);
          const lng = parseFloat(this.longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            this.mapCenter = { lat, lng };
            this.markerPosition = { lat, lng };
            return;
          }
        }
        // Otherwise set marker to user's current location first; it will update when user enters coordinates
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              this.mapCenter = { lat, lng };
              this.markerPosition = { lat, lng };
              this.latitude = lat.toFixed(6);
              this.longitude = lng.toFixed(6);
              this.reverseGeocode({ lat, lng });
            },
            (error) => {
              console.log('Geolocation error:', error);
              // Fallback: show marker at default center so user can move it or enter coordinates
              this.markerPosition = { ...this.mapCenter };
            }
          );
        } else {
          this.markerPosition = { ...this.mapCenter };
        }
      },
  
      onSave() {
        if (!this.$refs.locationForm.validate()) {
          this.$notify({
            title: 'Error',
            text: 'Please fill in all required fields',
            type: 'error'
          });
          return;
        }

        const farmLocation = {
          longitude: parseFloat(this.longitude),
          latitude: parseFloat(this.latitude),
          address: this.search || null,
          areaSize: this.areaSize,
          areaUnit: this.areaUnit
        };

        // Commit to Vuex store
        this.$store.dispatch('carbonCredit/setFarmLocation', farmLocation);
        // Signal that user explicitly saved - AddPlotTree (edit mode) will run plot search
        this.$store.dispatch('carbonCredit/setFarmLocationJustSaved');
        
        // Go back to Add Plot Tree (preserve edit context if present)
        this.navigateBackToAddPlotTree();
      },
  
      gotoCurrentLocation() {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              
              // Update map center and marker position
              this.mapCenter = { lat, lng };
              this.markerPosition = { lat, lng };
              
              // Update form fields
              this.latitude = lat.toFixed(6);
              this.longitude = lng.toFixed(6);

              // Reverse geocode to get the address
              this.reverseGeocode({ lat, lng });
            },
            (error) => {
              console.log('Geolocation error:', error);
              this.$notify({
                title: 'Error',
                text: 'Unable to get current location. Please check your browser permissions.',
                type: 'error'
              });
            }
          );
        } else {
          this.$notify({
            title: 'Error',
            text: 'Geolocation is not supported by this browser.',
            type: 'error'
          });
        }
      },
  
      onSearchInput(value) {
        if (value.length < 3) {
          this.autocompleteResults = [];
          return;
        }
  
        // Clear previous timeout
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }
  
        // Debounce the search
        this.searchTimeout = setTimeout(() => {
          this.performAutocompleteSearch(value);
        }, 300);
      },
  
      handleSearchBlur() {
        // Delay hiding autocomplete to allow for clicks
        setTimeout(() => {
          this.showAutocomplete = false;
        }, 200);
      },
  
      selectAutocompleteResult(result) {
        this.search = result.description;
        this.showAutocomplete = false;
        
        // Get the place details
        this.getPlaceDetails(result.place_id);
      },
  
      async performAutocompleteSearch(query) {
        try {
          // Wait for Google Maps API to be available
          if (typeof window.google === 'undefined' || !window.google.maps) {
            await this.loadGoogleMapsAPI();
          }
  
          const service = new window.google.maps.places.AutocompleteService();
          
          service.getPlacePredictions({
            input: query,
            types: ['geocode', 'establishment'],
            componentRestrictions: { country: [] } // Search worldwide
          }, (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              this.autocompleteResults = predictions;
            } else {
              this.autocompleteResults = [];
            }
          });
        } catch (error) {
          console.error('Autocomplete search error:', error);
          this.autocompleteResults = [];
        }
      },
  
      async getPlaceDetails(placeId) {
        try {
          const service = new window.google.maps.places.PlacesService(document.createElement('div'));
          
          service.getDetails({
            placeId: placeId,
            fields: ['geometry', 'formatted_address', 'address_components']
          }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              
              // Update form fields
              this.latitude = lat.toFixed(6);
              this.longitude = lng.toFixed(6);
              
              // Move marker to the selected location
              this.markerPosition = { lat, lng };
              
              // Center the map on the selected location
              this.mapCenter = { lat, lng };

              // Update the search input with the full address
              this.search = place.formatted_address;
            }
          });
        } catch (error) {
          console.error('Error getting place details:', error);
          this.$notify({
            title: 'Error',
            text: 'Error getting location details. Please try again.',
            type: 'error'
          });
        }
      },

      reverseGeocode(location) {
        if (typeof window.google === 'undefined' || !window.google.maps) {
          console.error('Google Maps API not loaded');
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location }, (results, status) => {
          if (status === 'OK') {
            if (results[0]) {
              this.search = results[0].formatted_address;
            } else {
              this.search = 'Address not found';
            }
          } else {
            console.error('Geocoder failed due to: ' + status);
            this.search = 'Address lookup failed';
          }
        });
      },
    }
  }
  </script>
  
  <style scoped>
  .breadcrumb-back-row {
    align-items: center;
    flex-wrap: nowrap;
    gap: 350px;
  }
  .breadcrumb-back-row ::v-deep .breadcrum {
    margin-bottom: 0;
    padding-top: 18px;
    padding-bottom: 18px;
    padding-right: 0;
  }
  .breadcrumb-back-link {
    font-size: 14px;
    line-height: 1.5;
    flex-shrink: 0;
  }

  .map-wrapper {
    position: relative;
    width: 100%;
    height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .map-search {
    position: absolute;
    top: 14%;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
    z-index: 2;
  }
  
  .search-container {
    position: absolute;
    width: 100%;
    left: 50%;
    transform: translateX(-50%);
    top: 18%;
    z-index: 3;
  }
  
  .map-inner {
    width: 96%;
    height: 100%;
    border-radius: 4px;
    overflow: hidden;
  }
  
  ::v-deep .v-input__slot {
    background: #fff !important;
    border-radius: 8px !important;
  }
  
  ::v-deep .v-text-field__details {
    margin-bottom: 8px;
  }
  
  ::v-deep .v-select__selections {
    padding-top: 8px;
  }
  
  ::v-deep .v-text-field .v-input__control{
    border-radius: 4px !important;
  }
  
  ::v-deep .bg-primary .v-input__slot{
    background-color: #03BD72 !important;
    border-color: #03BD72 !important;
  }
  ::v-deep .v-select.v-input--dense .v-select__selection--comma{
    color: #fff !important;
    border-color: #03BD72 !important;
  }
  
  ::v-deep .bg-primary .v-icon.v-icon{
    color: #fff !important;
  }

  /* Farm area row: input has no right radius; unit dropdown shows full text */
  .farm-area-input ::v-deep .v-input__control,
  .farm-area-input ::v-deep .v-input__slot { border-radius: 8px 0 0 8px !important; }
  .farm-area-unit ::v-deep .v-input__control,
  .farm-area-unit ::v-deep .v-input__slot { border-radius: 0 8px 8px 0 !important; }
  .farm-area-unit-col { min-width: 120px; }
  .farm-area-unit ::v-deep .v-select__selection,
  .farm-area-unit ::v-deep .v-select__selection--comma { white-space: nowrap; overflow: visible; max-width: none; text-overflow: clip; }
  .farm-area-unit ::v-deep .v-input__slot { padding-right: 8px !important; }
  .farm-area-unit ::v-deep .v-select__selections { padding-right: 4px !important; }

  .autocomplete-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 250px;
    overflow-y: auto;
    z-index: 1000;
    width: 60%;
    margin: auto;
    margin-top: 5%;
  }
  .autocomplete-list .v-list {
    padding: 0;
  }
  
  .autocomplete-item {
    cursor: pointer;
    padding: 8px 16px;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s;
    margin-top: 30px;
  }
  
  .autocomplete-item:hover {
    background-color: #f5f5f5;
  }
  
  .autocomplete-item:last-child {
    border-bottom: none;
  }
  
  .autocomplete-item .v-list-item__content {
    padding: 0;
  }
  
  .autocomplete-item .v-list-item__title {
    font-size: 14px;
    color: #333;
    line-height: 1.4;
  }
  </style>
  